import { Controller } from "@hotwired/stimulus"

// Renders a two-month calendar with presets and a manual entry footer.
// On apply, it writes start_date / end_date into the parent form's hidden fields,
// sets period to "custom" (or empty for All time), and submits the form.
export default class extends Controller {
  static targets = [
    "modal",
    "monthLabel",
    "daysGrid",
    "fromInput",
    "toInput",
    "presetButton",
    "formStartDate",
    "formEndDate",
  ]

  static values = {
    selectedStart: String,
    selectedEnd: String,
    viewMonth: String,        // anchor for left calendar, "YYYY-MM"
    activePreset: String,
    weekdaysShort: Array,
    monthsFull: Array,
    // Name of the period <select> field this modal writes to ("custom" on apply, "" on All time).
    // Set to empty string when the modal isn't paired with a period select (e.g. Upload Date).
    periodFieldName: { type: String, default: "period" },
  }

  connect() {
    // Initialise view month from selected start (or today).
    const anchor = this.selectedStartValue ? this.parseISO(this.selectedStartValue) : new Date()
    this.viewMonthValue = this.formatMonth(new Date(anchor.getFullYear(), anchor.getMonth(), 1))
    this.redraw()
  }

  open() {
    this.modalTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
  }

  close() {
    this.modalTarget.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
  }

  closeOnBackdrop(event) {
    if (event.target === this.modalTarget) {
      this.close()
    }
  }

  // --- preset selection -----------------------------------------------------

  selectPreset(event) {
    const preset = event.currentTarget.dataset.preset
    this.activePresetValue = preset

    if (preset === "all_time") {
      this.selectedStartValue = ""
      this.selectedEndValue = ""
    } else if (preset === "custom") {
      // Keep current dates so the user can refine them.
      this.fromInputTarget.focus()
    } else {
      const { start, end } = this.computePreset(preset)
      this.selectedStartValue = this.formatISO(start)
      this.selectedEndValue = this.formatISO(end)
      // Anchor the left calendar on the start month so the range is visible.
      this.viewMonthValue = this.formatMonth(new Date(start.getFullYear(), start.getMonth(), 1))
    }

    this.redraw()
  }

  computePreset(name) {
    const today = this.todayLocal()
    const end = today
    let start

    switch (name) {
      case "last_day":
        start = today
        break
      case "last_week":
        start = this.addDays(today, -6)
        break
      case "last_month":
        start = this.addDays(today, -29)
        break
      case "last_3_months":
        start = this.addDays(today, -89)
        break
      case "last_year":
        start = this.addDays(today, -364)
        break
      case "month_to_date":
        start = new Date(today.getFullYear(), today.getMonth(), 1)
        break
      case "quarter_to_date": {
        const quarterStartMonth = Math.floor(today.getMonth() / 3) * 3
        start = new Date(today.getFullYear(), quarterStartMonth, 1)
        break
      }
      default:
        start = today
    }

    return { start, end }
  }

  // --- calendar navigation --------------------------------------------------

  prevMonth() {
    this.viewMonthValue = this.shiftMonth(this.viewMonthValue, -1)
    this.redraw()
  }

  nextMonth() {
    this.viewMonthValue = this.shiftMonth(this.viewMonthValue, 1)
    this.redraw()
  }

  // --- day picking ----------------------------------------------------------

  pickDay(event) {
    const iso = event.currentTarget.dataset.date
    if (!iso) return

    const hasStart = this.selectedStartValue && !this.selectedEndValue
    const hasBoth = this.selectedStartValue && this.selectedEndValue

    if (!this.selectedStartValue || hasBoth) {
      // Start a fresh range.
      this.selectedStartValue = iso
      this.selectedEndValue = ""
    } else if (hasStart) {
      // Second click sets the end; swap if before start.
      if (this.compareISO(iso, this.selectedStartValue) < 0) {
        this.selectedEndValue = this.selectedStartValue
        this.selectedStartValue = iso
      } else {
        this.selectedEndValue = iso
      }
    }

    this.activePresetValue = "custom"
    this.redraw()
  }

  manualEntry() {
    const from = this.fromInputTarget.value
    const to = this.toInputTarget.value
    if (!from || !to) return

    if (this.compareISO(to, from) < 0) {
      this.selectedStartValue = to
      this.selectedEndValue = from
    } else {
      this.selectedStartValue = from
      this.selectedEndValue = to
    }
    this.activePresetValue = "custom"
    this.redraw()
  }

  // --- apply / cancel -------------------------------------------------------

  apply() {
    const form = this.formStartDateTarget.form
    if (!form) {
      this.close()
      return
    }

    const periodField = this.periodFieldNameValue ? form.elements.namedItem(this.periodFieldNameValue) : null

    if (this.activePresetValue === "all_time") {
      if (periodField) periodField.value = ""
      this.formStartDateTarget.value = ""
      this.formEndDateTarget.value = ""
    } else {
      if (!this.selectedStartValue || !this.selectedEndValue) {
        // Need both ends to apply a custom range.
        return
      }
      if (periodField) periodField.value = "custom"
      this.formStartDateTarget.value = this.selectedStartValue
      this.formEndDateTarget.value = this.selectedEndValue
    }

    this.close()
    form.requestSubmit()
  }

  // --- rendering ------------------------------------------------------------

  redraw() {
    // Sync footer date inputs with current state.
    if (this.hasFromInputTarget) this.fromInputTarget.value = this.selectedStartValue || ""
    if (this.hasToInputTarget) this.toInputTarget.value = this.selectedEndValue || ""

    // Highlight active preset.
    this.presetButtonTargets.forEach((btn) => {
      const active = btn.dataset.preset === this.activePresetValue
      btn.classList.toggle("bg-indigo-50", active)
      btn.classList.toggle("text-indigo-700", active)
      btn.classList.toggle("font-medium", active)
      btn.classList.toggle("text-gray-700", !active)
    })

    // Render both month panels.
    const left = this.parseMonth(this.viewMonthValue)
    const right = new Date(left.getFullYear(), left.getMonth() + 1, 1)
    this.renderMonth(0, left)
    this.renderMonth(1, right)
  }

  renderMonth(index, firstOfMonth) {
    if (!this.monthLabelTargets[index] || !this.daysGridTargets[index]) return

    const months = this.monthsFullValue
    const year = firstOfMonth.getFullYear()
    const month = firstOfMonth.getMonth()
    this.monthLabelTargets[index].textContent = `${months[month]} ${year}`

    const grid = this.daysGridTargets[index]
    grid.innerHTML = ""

    // Weekday headers (Mo-Su).
    this.weekdaysShortValue.forEach((d) => {
      const cell = document.createElement("div")
      cell.className = "text-center text-xs font-semibold text-gray-500 py-1"
      cell.textContent = d
      grid.appendChild(cell)
    })

    // Compute first cell — week starts Monday.
    // JS getDay(): 0 Sun .. 6 Sat. We want Mo=0, Tu=1, ... Su=6.
    const firstWeekday = (firstOfMonth.getDay() + 6) % 7
    const gridStart = new Date(year, month, 1 - firstWeekday)

    const startISO = this.selectedStartValue
    const endISO = this.selectedEndValue

    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i)
      const iso = this.formatISO(d)
      const inMonth = d.getMonth() === month
      const isStart = startISO && iso === startISO
      const isEnd = endISO && iso === endISO
      const inRange = startISO && endISO && this.compareISO(iso, startISO) > 0 && this.compareISO(iso, endISO) < 0

      const btn = document.createElement("button")
      btn.type = "button"
      btn.dataset.date = iso
      btn.dataset.action = "click->date-range-modal#pickDay"

      let classes = "w-9 h-9 text-sm rounded-md flex items-center justify-center transition-colors"
      if (isStart || isEnd) {
        classes += " bg-indigo-600 text-white font-semibold"
      } else if (inRange) {
        classes += " bg-indigo-100 text-indigo-700"
      } else if (inMonth) {
        classes += " text-gray-700 hover:bg-gray-100"
      } else {
        classes += " text-gray-300 hover:bg-gray-50"
      }
      btn.className = classes
      btn.textContent = String(d.getDate())
      grid.appendChild(btn)
    }
  }

  // --- date utilities -------------------------------------------------------

  todayLocal() {
    const t = new Date()
    return new Date(t.getFullYear(), t.getMonth(), t.getDate())
  }

  addDays(date, n) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + n)
  }

  parseISO(iso) {
    const [y, m, d] = iso.split("-").map(Number)
    return new Date(y, m - 1, d)
  }

  formatISO(date) {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, "0")
    const d = String(date.getDate()).padStart(2, "0")
    return `${y}-${m}-${d}`
  }

  parseMonth(value) {
    const [y, m] = value.split("-").map(Number)
    return new Date(y, m - 1, 1)
  }

  formatMonth(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`
  }

  shiftMonth(value, delta) {
    const d = this.parseMonth(value)
    return this.formatMonth(new Date(d.getFullYear(), d.getMonth() + delta, 1))
  }

  compareISO(a, b) {
    if (a === b) return 0
    return a < b ? -1 : 1
  }
}
